import React from 'react';
import { Repeat, Check, CalendarX, CalendarCheck } from 'lucide-react';
import { addYears, format, parseISO } from 'date-fns';
import { RecurrenceFrequency } from '../types/supabase';
import { FREQUENCIES, describeRecurrence, frequencyLabel } from '../lib/recurrence';

export interface RecurrenceValue {
  is_recurring: boolean;
  recurrence_frequency: RecurrenceFrequency | null;
  recurrence_end_date: string | null;
}

export const EMPTY_RECURRENCE: RecurrenceValue = {
  is_recurring: false,
  recurrence_frequency: null,
  recurrence_end_date: null,
};

interface RecurrenceFieldProps {
  startDate: string;
  time: string;
  value: RecurrenceValue;
  onChange: (next: RecurrenceValue) => void;
}

const RecurrenceField: React.FC<RecurrenceFieldProps> = ({ startDate, time, value, onChange }) => {
  const maxEndDate = startDate ? format(addYears(parseISO(startDate), 3), 'yyyy-MM-dd') : undefined;
  const hasEndDate = value.recurrence_end_date !== null;
  const summary = startDate && value.is_recurring
    ? describeRecurrence({ ...value, date: startDate, time })
    : null;

  const toggle = (checked: boolean) =>
    onChange(checked ? { is_recurring: true, recurrence_frequency: 'weekly', recurrence_end_date: null } : EMPTY_RECURRENCE);

  return (
    <fieldset className="space-y-3">
      <legend className="block text-sm font-medium text-gray-700 mb-1">Schedule</legend>
      <label
        className={`flex items-start gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-sm ${
          value.is_recurring ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={value.is_recurring}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span
          aria-hidden="true"
          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
            value.is_recurring ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
          }`}
        >
          {value.is_recurring && <Check className="h-3.5 w-3.5 text-white" />}
        </span>
        <span className="flex-1">
          <span className="flex items-center gap-1.5 font-medium text-gray-900">
            <Repeat className="h-4 w-4 text-blue-600" />
            This event repeats
          </span>
          <span className="block text-xs text-gray-600 mt-1">Choose how often it happens and when it stops</span>
        </span>
      </label>

      {value.is_recurring && (
        <div className="rounded-lg border border-blue-100 bg-white p-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">How often?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
              {FREQUENCIES.map((f) => {
                const selected = value.recurrence_frequency === f;
                return (
                  <button
                    key={f}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onChange({ ...value, recurrence_frequency: f })}
                    className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                      selected
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                  >
                    {frequencyLabel(f, startDate || undefined)}
                  </button>
                );
              })}
            </div>
            {!startDate && (
              <p className="text-xs text-gray-500 mt-2">Pick a date above to see the exact days.</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Ends</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                aria-pressed={!hasEndDate}
                onClick={() => onChange({ ...value, recurrence_end_date: null })}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                  !hasEndDate ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CalendarCheck className="h-4 w-4" />
                No end date
              </button>
              <button
                type="button"
                aria-pressed={hasEndDate}
                onClick={() => onChange({ ...value, recurrence_end_date: hasEndDate ? value.recurrence_end_date : '' })}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                  hasEndDate ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CalendarX className="h-4 w-4" />
                On a specific date
              </button>
            </div>
            {hasEndDate && (
              <div className="mt-3">
                <label htmlFor="recurrence_end_date" className="block text-xs font-medium text-gray-600 mb-1">
                  Last possible date
                </label>
                <input
                  id="recurrence_end_date"
                  type="date"
                  required
                  min={startDate || undefined}
                  max={maxEndDate}
                  value={value.recurrence_end_date ?? ''}
                  onChange={(e) => onChange({ ...value, recurrence_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {summary && (
            <p className="flex items-center gap-2 text-sm text-blue-800 bg-blue-50 rounded-md px-3 py-2">
              <Repeat className="h-4 w-4 flex-shrink-0" />
              {summary}
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default RecurrenceField;
